import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { TranslateService } from "@ngx-translate/core";
import { catchError, firstValueFrom, of } from "rxjs";
import { environment } from "src/environments/environment";

/**
 * QSL-10245: App-wide i18n orchestrator for the dual-language (Arabic/English)
 * web interface. Mirrors Siraj's translation source-of-truth — the server-side
 * `ui_translation` collection — but feeds the existing ngx-translate `| translate`
 * pipe instead of Siraj's per-component `t()`, so a single language switch
 * re-renders every component (including shared widgets) at once.
 *
 * Scope strategy (solves "same component used under multiple roles"):
 *   - `shared`  → nav/buttons/common labels; loaded at app boot, available to ALL roles.
 *   - `teacher` → teacher-only screens; merged on entering the teacher area.
 * Each scope is fetched from GET /languages/translations/{scope}/{lang} and MERGED
 * on top of whatever ngx-translate already holds (the existing per-org vocabulary
 * loaded from localStorage by TranslateLSLoader stays intact — this is additive).
 */

/** Languages that render right-to-left. Mirrors the server/Siraj RTL set. */
const RTL_LANGS = new Set(["ar", "ur", "fa", "ps", "he", "ckb", "ug"]);

/** Always-loaded scope holding cross-role shared strings (nav, buttons, common). */
export const SHARED_SCOPE = "shared";

/** localStorage key persisting the user's chosen UI language across reloads. */
const LANG_STORAGE_KEY = "uiLang";

@Injectable({ providedIn: "root" })
export class AppI18nService {
  /** Scopes whose strings should be present in the dictionary (shared is always on). */
  private readonly activeScopes = new Set<string>([SHARED_SCOPE]);

  /** Guards against re-fetching the same `${scope}:${lang}` more than once. */
  private readonly loaded = new Set<string>();

  /** The active two-letter UI language code. */
  currentLang = "en";

  constructor(
    private readonly http: HttpClient,
    private readonly translate: TranslateService
  ) {
    // QSL-10245: apply text direction synchronously at app construction (before
    // app.component's async bootstrap work) so an Arabic user sees RTL on first
    // paint and deep-links render RTL immediately — not only after a toggle.
    const saved = this.normalize(localStorage.getItem(LANG_STORAGE_KEY) || "en");
    this.currentLang = saved;
    this.applyDir(saved);
  }

  /** Current text direction derived from the active language. */
  get currentDir(): "rtl" | "ltr" {
    return RTL_LANGS.has(this.currentLang) ? "rtl" : "ltr";
  }

  /** Whether a given language (defaults to the active one) is right-to-left. */
  isRTL(lang: string = this.currentLang): boolean {
    return RTL_LANGS.has(lang);
  }

  /**
   * Call once at app boot. Resolves the language (explicit arg → saved choice →
   * ngx-translate default → 'en'), loads the shared scope, and applies direction.
   */
  async init(lang?: string): Promise<void> {
    const resolved = this.normalize(
      lang ||
        localStorage.getItem(LANG_STORAGE_KEY) ||
        this.translate.getDefaultLang() ||
        "en"
    );
    this.currentLang = resolved;
    this.translate.setDefaultLang("en"); // English is the fallback for any missing key
    await this.activate(resolved);
  }

  /**
   * Register an area's scope (e.g. 'teacher') and load it in the active language.
   * Idempotent — safe to call on every entry into the area.
   */
  async useScope(scope: string): Promise<void> {
    this.activeScopes.add(scope);
    // Reload ALL active scopes (not just this one): the app's post-login language
    // setup reloads translations[lang] from localStorage vocab, wiping scopes
    // merged earlier (e.g. `shared` at boot). Re-merging every active scope here
    // restores them. Then re-emit so rendered | translate pipes pick them up.
    await this.loadActiveScopes(this.currentLang);
    this.translate.use(this.currentLang);
  }

  /**
   * Switch the UI language. Reloads every active scope in the new language, flips
   * the ngx-translate active language (re-rendering all `| translate` pipes), and
   * updates the document direction. Persists the choice to localStorage, and — when
   * `persist` is true (the default, i.e. a user-initiated change) — to the server's
   * `user.languageCode` (the cross-surface source of truth) so it round-trips to
   * mobile/Siraj and survives a fresh browser. Pass `persist=false` when the new
   * language was just READ from the server (see applyServerLanguage).
   */
  async switchLanguage(lang: string, persist = true): Promise<void> {
    const resolved = this.normalize(lang);
    if (resolved === this.currentLang) return;
    this.currentLang = resolved;
    localStorage.setItem(LANG_STORAGE_KEY, resolved);
    if (persist) this.persistLanguageCode(resolved);
    await this.activate(resolved);
  }

  /**
   * QSL-10245: seed the UI language from the authenticated user's stored
   * `languageCode` (the source of truth). READ-only — never writes back. Call
   * once the current user is known (scope to teachers at the call site so the
   * untranslated admin shells are not flipped). No-op when the code is absent or
   * already active.
   */
  async applyServerLanguage(languageCode?: string | null): Promise<void> {
    if (!languageCode) return;
    const resolved = this.normalize(languageCode);
    if (resolved === this.currentLang) {
      this.applyDir(resolved); // already correct — just ensure direction is set
      return;
    }
    await this.switchLanguage(resolved, /* persist */ false);
  }

  /**
   * Fire-and-forget PATCH of the user's languageCode. Mirrors the teacher
   * settings UserService (FormData → PATCH /users). Errors are swallowed so a
   * failed persist never blocks the in-app language switch.
   */
  private persistLanguageCode(lang: string): void {
    const form = new FormData();
    form.append("languageCode", lang);
    this.http
      .patch(`${environment.apiURL}/users`, form)
      .pipe(catchError(() => of(null)))
      .subscribe();
  }

  /** Shared boot/switch path: ensure the loader can emit, switch lang, merge scopes, set dir. */
  private async activate(lang: string): Promise<void> {
    this.ensureLoaderSeed(lang);
    // Apply direction synchronously UP-FRONT (before the async scope loads) so the
    // last language *decision* wins by call order — not by whichever activate()'s
    // async tail finishes last. A fresh-login race otherwise let app-boot's default
    // init('en') apply ltr AFTER the teacher login-read applied 'ar', leaving
    // uiLang=ar but dir=ltr. The constructor already sets dir this way on load.
    this.applyDir(lang);
    await firstValueFrom(this.translate.use(lang));
    await this.loadActiveScopes(lang);
    // Re-emit after the scopes are merged so pipes that already rendered (with
    // raw keys) re-evaluate against the now-loaded dictionary.
    this.translate.use(lang);
  }

  private loadActiveScopes(lang: string): Promise<unknown> {
    return Promise.all([...this.activeScopes].map((s) => this.loadScope(s, lang)));
  }

  /** Fetch one scope from ui_translation and MERGE it into the active dictionary. */
  private async loadScope(scope: string, lang: string): Promise<void> {
    const res = await firstValueFrom(
      this.http
        .get<{ scope: string; langCode: string; strings: Record<string, string> }>(
          `${environment.apiURL}/languages/translations/${scope}/${lang}`
        )
        .pipe(catchError(() => of(null))) // never let a missing scope break the UI
    );
    if (res?.strings && Object.keys(res.strings).length > 0) {
      // merge = true → add these keys on top of existing vocab + other scopes.
      // Always re-merge (no cache): the app may reset translations[lang] from
      // localStorage vocab after login, so scopes must be re-applied on demand.
      this.translate.setTranslation(lang, res.strings, true);
    }
  }

  /**
   * The existing TranslateLSLoader reads localStorage[lang] and only emits when a
   * value is present (and never completes otherwise). For any language without a
   * seeded vocabulary (e.g. 'ar'), seed an empty object so translate.use() resolves
   * instead of hanging; the real strings then arrive via loadScope().
   */
  private ensureLoaderSeed(lang: string): void {
    if (!localStorage.getItem(lang)) {
      localStorage.setItem(lang, "{}");
    }
  }

  private applyDir(lang: string): void {
    const dir = RTL_LANGS.has(lang) ? "rtl" : "ltr";
    const html = document.documentElement;
    html.setAttribute("dir", dir);
    html.setAttribute("lang", lang);
  }

  private normalize(lang: string): string {
    return (lang || "en").substring(0, 2).toLowerCase();
  }
}
